import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isPlanError } from '../../components/plan/planErrors';
import { Upsell, UpsellFromError } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { formatInstantDate } from '../../lib/format';
import { PLATFORM_OPTIONS, platformLabel } from '../../lib/platforms';
import type { LinkedInAuthorizeRead, PlatformConnectionRead, PlatformName } from '../../types/api';
import { useBusiness } from '../onboarding/useBusiness';
import {
  authorizeFacebook,
  authorizeLinkedIn,
  disconnectFacebook,
  disconnectLinkedIn,
} from './integrationsApi';
import { SectionShell } from './SectionShell';
import { useConnections } from './useConnections';

/**
 * Value of the `<platform>=` query param a callback route hands back on a
 * successful connect, so the section can say so once. Stripped as soon as it
 * has been read — a reload should not congratulate the user again.
 */
const CONNECTED_VALUE = 'connected';

/** What differs between the platforms we publish to. Everything else is shared. */
interface PublishableConfig {
  authorize: (businessId: string) => Promise<LinkedInAuthorizeRead>;
  disconnect: (businessId: string) => Promise<void>;
  /** Shown when the connection carries no display name. */
  fallbackName: string;
  /** Appended to the name when the connection posts as an organization. */
  organizationSuffix: string;
  /** The unconnected card's one line. */
  pitch: string;
}

/** The platforms v1 publishes to. The others are cards, not features. */
const PUBLISHABLE: Partial<Record<PlatformName, PublishableConfig>> = {
  linkedin: {
    authorize: authorizeLinkedIn,
    disconnect: disconnectLinkedIn,
    fallbackName: 'your LinkedIn profile',
    organizationSuffix: ' (company page)',
    pitch: 'Connect a profile and a finished LinkedIn post can go out from the review screen.',
  },
  facebook: {
    authorize: authorizeFacebook,
    disconnect: disconnectFacebook,
    fallbackName: 'your Facebook Page',
    organizationSuffix: '',
    pitch: 'Connect a Page and a finished Facebook post can go out from the review screen.',
  },
};

/** Message for the domain errors starting or ending a connection can answer with. */
function connectionMessage(caught: unknown, fallback: string, label: string): string {
  if (caught instanceof ApiError) {
    if (caught.code === 'INTEGRATION_DISABLED') {
      return `${label} publishing is not configured on this server.`;
    }
    if (caught.status === 403) {
      return 'Only the account owner can change connected accounts.';
    }
  }
  return getErrorMessage(caught, fallback);
}

function statusLabel(connection: PlatformConnectionRead): string {
  if (!connection.available) return 'Unavailable';
  switch (connection.status) {
    case 'connected':
      return connection.expiring_soon ? 'Expiring' : 'Connected';
    case 'expired':
      return 'Expired';
    case 'unconnected':
      return 'Not connected';
  }
}

function statusClassName(connection: PlatformConnectionRead): string {
  const base = 'integration-card__status';
  if (!connection.available) return `${base} ${base}--idle`;
  switch (connection.status) {
    case 'connected':
      return connection.expiring_soon ? `${base} ${base}--warning` : `${base} ${base}--live`;
    case 'expired':
      return `${base} ${base}--warning`;
    case 'unconnected':
      return `${base} ${base}--idle`;
  }
}

/**
 * A platform we cannot publish to yet. It is on screen because the list of
 * places content goes is the honest answer to "what can this connect to", and
 * an absent card would read as an oversight rather than a boundary.
 */
function ComingSoonCard({ platform }: { platform: PlatformName }) {
  return (
    <li className="integration-card integration-card--quiet">
      <div className="integration-card__head">
        <h3 className="integration-card__name">{platformLabel(platform)}</h3>
        <span className="integration-card__status integration-card__status--idle">Coming soon</span>
      </div>
      <p className="integration-card__detail">
        Download the finished post and put it out yourself. Publishing here is not built yet.
      </p>
      <div className="integration-card__actions">
        <button className="button" type="button" disabled>
          Connect
        </button>
      </div>
    </li>
  );
}

interface PublishableCardProps {
  businessId: string;
  connection: PlatformConnectionRead;
  config: PublishableConfig;
  isOwner: boolean;
  onChanged: () => void;
}

/**
 * A publishable connection (LinkedIn, Facebook): connect, reconnect, disconnect.
 *
 * Connecting leaves the page for the platform's own consent screen and comes
 * back to `/integrations/<linkedin|meta>/callback`. Nothing is written to storage on the way
 * out — the signed `state` inside the authorization URL carries the business and
 * the user, and it is the only thing that has to survive the round trip.
 *
 * There is no refresh token to have: LinkedIn issues them to approved marketing
 * partners only, so a token runs out after about 60 days and reconnecting is the
 * real workflow rather than an error path. The card says when that is due. A
 * Facebook Page token does not expire, so its `expires_at` is null and no
 * expiry line shows.
 */
function PublishableCard({
  businessId,
  connection,
  config,
  isOwner,
  onChanged,
}: PublishableCardProps) {
  const label = platformLabel(connection.platform);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isConfirmingDisconnect, setIsConfirmingDisconnect] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planError, setPlanError] = useState<ApiError | null>(null);
  // Connecting is for publishing, a plan feature. A connection already made
  // is kept on any plan (and can still be disconnected); only new ones wait.
  const { can } = usePlan();
  const canPublish = can('publish');

  const isBusy = isConnecting || isDisconnecting;
  const isConnected = connection.status === 'connected';
  const isExpired = connection.status === 'expired';

  function handleConnect() {
    if (isBusy || !canPublish) return;
    setError(null);
    setPlanError(null);
    setIsConnecting(true);

    void (async () => {
      try {
        const authorization = await config.authorize(businessId);
        // This tab is leaving. Deliberately not clearing the busy flag: the
        // button stays disabled until the navigation takes.
        window.location.assign(authorization.authorization_url);
      } catch (caught) {
        setIsConnecting(false);
        if (isPlanError(caught)) setPlanError(caught);
        else setError(connectionMessage(caught, `Could not start the ${label} connection.`, label));
      }
    })();
  }

  function handleDisconnect() {
    if (isBusy) return;
    setError(null);
    setIsDisconnecting(true);

    void (async () => {
      try {
        await config.disconnect(businessId);
        setIsDisconnecting(false);
        setIsConfirmingDisconnect(false);
        onChanged();
      } catch (caught) {
        setIsDisconnecting(false);
        setError(connectionMessage(caught, `Could not disconnect ${label}.`, label));
      }
    })();
  }

  return (
    <li className="integration-card">
      <div className="integration-card__head">
        <h3 className="integration-card__name">{label}</h3>
        <span className={statusClassName(connection)}>{statusLabel(connection)}</span>
      </div>

      {!connection.available ? (
        <p className="integration-card__detail integration-card__detail--muted">
          Not configured on this server.
        </p>
      ) : isConnected ? (
        <>
          <p className="integration-card__detail">
            Posting as <strong>{connection.author_name ?? config.fallbackName}</strong>
            {connection.author_kind === 'organization' ? config.organizationSuffix : null}.
          </p>
          {connection.expires_at ? (
            <p
              className={
                connection.expiring_soon
                  ? 'integration-card__expiry integration-card__expiry--warning'
                  : 'integration-card__expiry'
              }
            >
              Expires {formatInstantDate(connection.expires_at)}
              {connection.expiring_soon ? ' — reconnect to keep publishing.' : '.'}
            </p>
          ) : null}
        </>
      ) : isExpired ? (
        <p className="integration-card__detail integration-card__detail--warning">
          Connection expired, reconnect to keep publishing.
        </p>
      ) : (
        <p className="integration-card__detail">{config.pitch}</p>
      )}

      {connection.available ? (
        <div className="integration-card__actions">
          {isOwner ? (
            <>
              <button
                className={isConnected ? 'button' : 'button button--primary'}
                type="button"
                disabled={isBusy || !canPublish}
                onClick={handleConnect}
              >
                {isConnecting
                  ? `Opening ${label}`
                  : isConnected || isExpired
                    ? 'Reconnect'
                    : `Connect ${label}`}
              </button>

              {isConnected || isExpired ? (
                <button
                  className="button button--danger"
                  type="button"
                  disabled={isBusy || isConfirmingDisconnect}
                  onClick={() => setIsConfirmingDisconnect(true)}
                >
                  Disconnect
                </button>
              ) : null}
            </>
          ) : (
            <p className="integration-card__detail integration-card__detail--muted">
              {isConnected
                ? 'Only the account owner can change this connection.'
                : isExpired
                  ? 'Ask the owner to reconnect.'
                  : 'Ask the owner to connect.'}
            </p>
          )}
        </div>
      ) : null}

      {connection.available && isOwner && !canPublish ? <Upsell feature="publish" /> : null}

      {isConfirmingDisconnect ? (
        <div className="integration-card__confirm">
          <p className="integration-card__confirm-message">
            Disconnect {label}? Nothing already published changes. This stops us posting for you
            until it is connected again.
          </p>
          <div className="integration-card__confirm-actions">
            <button
              className="button button--danger"
              type="button"
              disabled={isDisconnecting}
              onClick={handleDisconnect}
            >
              {isDisconnecting ? 'Disconnecting' : 'Yes, disconnect'}
            </button>
            <button
              className="button"
              type="button"
              disabled={isDisconnecting}
              onClick={() => setIsConfirmingDisconnect(false)}
            >
              Keep it
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}
      <UpsellFromError error={planError} />
    </li>
  );
}

/**
 * Connected accounts — the platforms a finished post can be published to.
 *
 * Owns no records of its own, like the Looks tab: the rows it
 * draws are the server's view of five platforms, and the only writes on the
 * screen are connect and disconnect. Exactly one of the five does anything in
 * v1; the rest are cards saying so, because export remains the way content
 * leaves this product everywhere else.
 *
 * Connecting is owner-only, matching the membership roles already in the schema.
 * A member sees the same state read-only — which account a post would go out as
 * is part of reviewing it.
 */
export function IntegrationsSection({ businessId }: { businessId: string }) {
  const { business } = useBusiness();
  const { connections, isLoading, error, refresh } = useConnections(businessId);
  const [searchParams, setSearchParams] = useSearchParams();

  // Latched on the first render, so stripping the param below does not retract
  // the notice it announced.
  const [justConnected] = useState(() =>
    PLATFORM_OPTIONS.find(
      (option) => PUBLISHABLE[option.value] && searchParams.get(option.value) === CONNECTED_VALUE,
    ),
  );

  useEffect(() => {
    if (!justConnected || searchParams.get(justConnected.value) !== CONNECTED_VALUE) return;
    const next = new URLSearchParams(searchParams);
    next.delete(justConnected.value);
    setSearchParams(next, { replace: true });
  }, [justConnected, searchParams, setSearchParams]);

  const isOwner = business?.role === 'owner';

  return (
    <SectionShell
      title="Connected accounts"
      lede="Where finished posts go. LinkedIn and Facebook posts publish directly; the rest download for you to post yourself."
      isLoading={isLoading}
      error={error}
      isEmpty={false}
      emptyMessage=""
    >
      {justConnected ? (
        <p className="form__notice" role="status">
          {justConnected.label} is connected. A finished {justConnected.label} post now offers
          Publish beside Download.
        </p>
      ) : null}

      <ul className="integrations-grid">
        {PLATFORM_OPTIONS.map((option) => {
          const connection = connections?.find((entry) => entry.platform === option.value) ?? null;

          const config = PUBLISHABLE[option.value];

          if (!config || connection === null) {
            return <ComingSoonCard key={option.value} platform={option.value} />;
          }

          return (
            <PublishableCard
              key={option.value}
              businessId={businessId}
              connection={connection}
              config={config}
              isOwner={isOwner}
              onChanged={refresh}
            />
          );
        })}
      </ul>

      <p className="integration-note">
        A LinkedIn token lasts about 60 days and cannot be renewed in the background, so expect to
        reconnect a few times a year. Nothing publishes on its own: approving a draft still only
        approves it.
      </p>
    </SectionShell>
  );
}
